import { getCheckoutOptions } from './checkouts';

export const BOT_LEVELS = {
  1: {
    name: 'Principiante',
    emoji: '🟢',
    description: 'Acaba de empezar. Tira al tablero y a ver dónde cae.',
    targetAvg: 28,
    accuracy: 0.15,
    checkoutRate: 0.05,
    chance180: 0,
    scoringBias: 1.05,
    setupBias: 1.05,
    checkoutBias: 1.22,
    checkoutPressureBias: 0.96,
  },
  2: {
    name: 'Amateur',
    emoji: '🟡',
    description: 'Juega de vez en cuando. Va pillándole el truco.',
    targetAvg: 38,
    accuracy: 0.22,
    checkoutRate: 0.10,
    chance180: 0.001,
    scoringBias: 1.02,
    setupBias: 1.02,
    checkoutBias: 1.08,
    checkoutPressureBias: 0.97,
  },
  3: {
    name: 'Intermedio',
    emoji: '🟠',
    description: 'Jugador de liga local. Consistente y con rachas buenas.',
    targetAvg: 48,
    accuracy: 0.32,
    checkoutRate: 0.16,
    chance180: 0.005,
    scoringBias: 1,
    setupBias: 1,
    checkoutBias: 1.02,
    checkoutPressureBias: 0.97,
  },
  4: {
    name: 'Avanzado',
    emoji: '🔴',
    description: 'Competitivo. Buen promedio y cierres sólidos.',
    targetAvg: 60,
    accuracy: 0.44,
    checkoutRate: 0.25,
    chance180: 0.012,
    scoringBias: 1,
    setupBias: 1,
    checkoutBias: 1,
    checkoutPressureBias: 0.96,
  },
  5: {
    name: 'Experto',
    emoji: '🟣',
    description: 'Nivel campeonato provincial. Alta consistencia.',
    targetAvg: 72,
    accuracy: 0.55,
    checkoutRate: 0.32,
    chance180: 0.025,
    scoringBias: 0.99,
    setupBias: 1,
    checkoutBias: 1,
    checkoutPressureBias: 0.95,
  },
  6: {
    name: 'Profesional',
    emoji: '💎',
    description: 'Nivel circuito profesional. Triples por costumbre.',
    targetAvg: 85,
    accuracy: 0.72,
    checkoutRate: 0.44,
    chance180: 0.05,
    scoringBias: 0.98,
    setupBias: 0.99,
    checkoutBias: 0.97,
    checkoutPressureBias: 0.94,
  },
  7: {
    name: 'Leyenda',
    emoji: '👑',
    description: 'Élite mundial. Van Gerwen, Humphries, Littler.',
    targetAvg: 100,
    accuracy: 0.82,
    checkoutRate: 0.52,
    chance180: 0.09,
    scoringBias: 1.07,
    setupBias: 1.05,
    checkoutBias: 1.03,
    checkoutPressureBias: 0.88,
  },
  8: {
    name: 'No Eres Humano',
    emoji: '🛸',
    description: 'Más allá de lo posible. Precisión sobrenatural.',
    targetAvg: 110,
    accuracy: 0.92,
    checkoutRate: 0.65,
    chance180: 0.16,
    scoringBias: 1.16,
    setupBias: 1.10,
    checkoutBias: 1.06,
    checkoutPressureBias: 0.84,
  },
};

const BOARD_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const BOGEY_NUMBERS = new Set([159, 162, 163, 165, 166, 168, 169]);
const PREFERRED_DOUBLE_LEAVES = new Set([40, 32, 36, 24, 16, 20, 12, 8, 4, 2]);

const SETUP_CANDIDATES = [
  { type: 'T', num: 20 },
  { type: 'T', num: 19 },
  { type: 'T', num: 18 },
  { type: 'T', num: 17 },
  { type: 'T', num: 16 },
  { type: 'T', num: 15 },
  { type: 'S', num: 20 },
  { type: 'S', num: 19 },
  { type: 'S', num: 18 },
  { type: 'S', num: 17 },
  { type: 'S', num: 16 },
  { type: 'S', num: 15 },
  { type: 'S', num: 14 },
  { type: 'S', num: 13 },
  { type: 'S', num: 12 },
  { type: 'S', num: 11 },
  { type: 'S', num: 10 },
  { type: 'S', num: 9 },
  { type: 'S', num: 8 },
  { type: 'S', num: 7 },
  { type: 'S', num: 6 },
  { type: 'S', num: 5 },
  { type: 'S', num: 4 },
  { type: 'S', num: 3 },
  { type: 'S', num: 2 },
  { type: 'S', num: 1 },
  { type: 'S', num: 25 },
];

const NEIGHBORS = BOARD_ORDER.reduce((acc, num, index) => {
  acc[num] = [
    BOARD_ORDER[(index - 1 + BOARD_ORDER.length) % BOARD_ORDER.length],
    BOARD_ORDER[(index + 1) % BOARD_ORDER.length],
  ];
  return acc;
}, {});

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function weightedPick(entries) {
  const normalizedEntries = entries
    .map((entry) => ({ ...entry, weight: Math.max(0, entry.weight) }))
    .filter((entry) => entry.weight > 0);

  if (normalizedEntries.length === 0) {
    return entries[0].value;
  }

  const totalWeight = normalizedEntries.reduce((sum, entry) => sum + entry.weight, 0);
  let roll = Math.random() * totalWeight;

  for (const entry of normalizedEntries) {
    roll -= entry.weight;
    if (roll <= 0) {
      return entry.value;
    }
  }

  return normalizedEntries[normalizedEntries.length - 1].value;
}

function randomSingle() {
  return BOARD_ORDER[Math.floor(Math.random() * 20)];
}

function getSkillRatio(profile) {
  return clamp((profile.targetAvg - BOT_LEVELS[1].targetAvg) / (BOT_LEVELS[8].targetAvg - BOT_LEVELS[1].targetAvg), 0, 1);
}

function getTurnForm(profile) {
  const skill = getSkillRatio(profile);
  const baseSwing = 0.18 - skill * 0.08;
  let form = 1 + (Math.random() * 2 - 1) * baseSwing;

  if (Math.random() < 0.08) {
    const streakSwing = 0.12 - skill * 0.04;
    form += (Math.random() * 2 - 1) * streakSwing;
  }

  return clamp(form, 0.72, 1.24);
}

function getTargetScore(target) {
  switch (target.type) {
    case 'T':
      return target.num * 3;
    case 'D':
      return target.num * 2;
    case 'DB':
      return 50;
    case 'S':
    default:
      return target.num;
  }
}

function getPreferredDoubleBonus(leave) {
  if (leave === 50) return 16;
  if (!PREFERRED_DOUBLE_LEAVES.has(leave)) return 0;
  if (leave === 40 || leave === 32) return 20;
  if (leave === 36 || leave === 24 || leave === 16) return 16;
  return 12;
}

function getLeaveQuality(leave) {
  if (leave < 2 || leave === 1) return -1000;
  if (BOGEY_NUMBERS.has(leave)) return -220;

  if (leave === 50 || (leave <= 40 && leave % 2 === 0)) {
    return 150 + getPreferredDoubleBonus(leave);
  }

  const routes = getCheckoutOptions(leave);
  if (routes.length === 0) {
    return leave > 170 ? 0 : -60;
  }

  const route = parseCheckoutRoute(routes[0]);
  const trebleCount = route.filter((dart) => dart.type === 'T').length;
  const bullCount = route.filter((dart) => dart.type === 'DB').length;
  const singleCount = route.filter((dart) => dart.type === 'S').length;

  let quality = route.length === 2 ? 96 : 58;
  quality -= trebleCount * 16;
  quality -= bullCount * 10;
  quality -= Math.max(0, singleCount - 1) * 4;

  if (leave > 120) quality -= 8;
  if (leave <= 70) quality += 8;

  return quality;
}

function throwAtScoringTriple(targetNum, profile, turnForm, underCheckoutPressure = false) {
  const skill = getSkillRatio(profile);
  const hot = turnForm - 1;
  const [left, right] = NEIGHBORS[targetNum] || [1, 5];
  const scoringBias = profile.scoringBias ?? 1;
  const maxTripleProb = clamp(0.66 + Math.max(0, scoringBias - 1) * 0.5 + skill * 0.02, 0.66, 0.74);

  let tripleProb = clamp((0.07 + skill * 0.53) * scoringBias * (1 + hot * 0.95), 0.03, maxTripleProb);
  let singleProb = clamp((0.28 + skill * 0.10) * (1 + (scoringBias - 1) * 0.35) * (1 + hot * 0.18), 0.22, 0.42);
  let neighborTripleProb = clamp((0.002 + skill * 0.028) * (1 + Math.max(0, hot) * 0.35), 0.001, 0.045);
  let missProb = clamp((0.17 - skill * 0.13) / (1 + Math.max(0, scoringBias - 1) * 1.4) * (1 - hot * 0.90), 0.012, 0.17);

  if (underCheckoutPressure) {
    tripleProb *= 0.64 * (profile.checkoutPressureBias ?? 1);
    neighborTripleProb *= 0.88;
    singleProb *= 1.08;
    missProb *= 1.24;
  }

  const reservedForSingles = 0.08;
  const occupied = tripleProb + singleProb + neighborTripleProb + missProb;
  if (occupied > 1 - reservedForSingles) {
    const scale = (1 - reservedForSingles) / occupied;
    tripleProb *= scale;
    singleProb *= scale;
    neighborTripleProb *= scale;
    missProb *= scale;
  }

  const neighborSingleProb = 1 - tripleProb - singleProb - neighborTripleProb - missProb;

  return weightedPick([
    { value: { score: targetNum * 3, isDouble: false }, weight: tripleProb },
    { value: { score: targetNum, isDouble: false }, weight: singleProb },
    { value: { score: left * 3, isDouble: false }, weight: neighborTripleProb / 2 },
    { value: { score: right * 3, isDouble: false }, weight: neighborTripleProb / 2 },
    { value: { score: left, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: right, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: 0, isDouble: false }, weight: missProb },
  ]);
}

function throwAtSingleTarget(targetNum, profile, turnForm, isCheckoutSingle) {
  const skill = getSkillRatio(profile);
  const hot = turnForm - 1;

  if (targetNum === 25) {
    let bullProb = clamp((0.06 + skill * 0.06) * (1 + hot * 0.30), 0.02, 0.16);
    let singleBullProb = clamp((0.54 + skill * 0.16) * (1 + hot * 0.18), 0.42, 0.78);
    let boardProb = clamp((0.18 - skill * 0.06) * (1 - hot * 0.10), 0.08, 0.20);

    const occupied = bullProb + singleBullProb + boardProb;
    if (occupied > 0.92) {
      const scale = 0.92 / occupied;
      bullProb *= scale;
      singleBullProb *= scale;
      boardProb *= scale;
    }

    const missProb = 1 - bullProb - singleBullProb - boardProb;

    return weightedPick([
      { value: { score: 50, isDouble: true }, weight: bullProb },
      { value: { score: 25, isDouble: false }, weight: singleBullProb },
      { value: { score: randomSingle(), isDouble: false }, weight: boardProb },
      { value: { score: 0, isDouble: false }, weight: missProb },
    ]);
  }

  const [left, right] = NEIGHBORS[targetNum] || [1, 5];
  const baseSingle = isCheckoutSingle
    ? (targetNum <= 10 ? 0.60 : 0.56)
    : 0.58;

  let singleProb = clamp((baseSingle + skill * 0.18) * (1 + hot * 0.22), 0.48, 0.82);
  let tripleProb = clamp((0.05 + skill * 0.08) * (1 + hot * 0.35), 0.02, 0.16);
  let neighborSingleProb = clamp((0.24 - skill * 0.10) * (1 - hot * 0.15), 0.08, 0.24);

  const occupied = singleProb + tripleProb + neighborSingleProb;
  if (occupied > 0.92) {
    const scale = 0.92 / occupied;
    singleProb *= scale;
    tripleProb *= scale;
    neighborSingleProb *= scale;
  }

  const missProb = 1 - singleProb - tripleProb - neighborSingleProb;

  return weightedPick([
    { value: { score: targetNum, isDouble: false }, weight: singleProb },
    { value: { score: targetNum * 3, isDouble: false }, weight: tripleProb },
    { value: { score: left, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: right, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: 0, isDouble: false }, weight: missProb },
  ]);
}

function throwAtCheckoutDouble(targetNum, profile, turnForm) {
  const skill = getSkillRatio(profile);
  const hot = turnForm - 1;
  const [left, right] = NEIGHBORS[targetNum] || [1, 5];
  const checkoutBias = profile.checkoutBias ?? 1;
  const comfortBonus = targetNum === 20 || targetNum === 16 || targetNum === 10 || targetNum === 8
    ? 0.04
    : targetNum <= 4
      ? -0.02
      : 0;

  let hitProb = clamp((profile.checkoutRate + comfortBonus + 0.03 + skill * 0.04) * checkoutBias * (1 + hot * 0.50), 0.06, 0.82);
  let singleProb = clamp((0.31 - skill * 0.08) * (1 - hot * 0.08), 0.14, 0.32);
  let neighborSingleProb = clamp((0.18 - skill * 0.07) * (1 - hot * 0.08), 0.05, 0.18);

  const occupied = hitProb + singleProb + neighborSingleProb;
  if (occupied > 0.94) {
    const scale = 0.94 / occupied;
    hitProb *= scale;
    singleProb *= scale;
    neighborSingleProb *= scale;
  }

  const missProb = 1 - hitProb - singleProb - neighborSingleProb;

  return weightedPick([
    { value: { score: targetNum * 2, isDouble: true }, weight: hitProb },
    { value: { score: targetNum, isDouble: false }, weight: singleProb },
    { value: { score: left, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: right, isDouble: false }, weight: neighborSingleProb / 2 },
    { value: { score: 0, isDouble: false }, weight: missProb },
  ]);
}

function throwAtCheckoutBull(profile, turnForm) {
  const skill = getSkillRatio(profile);
  const hot = turnForm - 1;
  const checkoutBias = profile.checkoutBias ?? 1;

  let hitProb = clamp((profile.checkoutRate * 0.65 + 0.02 + skill * 0.03) * checkoutBias * (1 + hot * 0.45), 0.04, 0.60);
  let outerBullProb = clamp((0.36 - skill * 0.10) * (1 - hot * 0.10), 0.18, 0.36);
  let boardProb = clamp((0.20 - skill * 0.06) * (1 - hot * 0.08), 0.08, 0.22);

  const occupied = hitProb + outerBullProb + boardProb;
  if (occupied > 0.92) {
    const scale = 0.92 / occupied;
    hitProb *= scale;
    outerBullProb *= scale;
    boardProb *= scale;
  }

  const missProb = 1 - hitProb - outerBullProb - boardProb;

  return weightedPick([
    { value: { score: 50, isDouble: true }, weight: hitProb },
    { value: { score: 25, isDouble: false }, weight: outerBullProb },
    { value: { score: randomSingle(), isDouble: false }, weight: boardProb },
    { value: { score: 0, isDouble: false }, weight: missProb },
  ]);
}

function parseCheckoutRoute(routeStr) {
  return routeStr.split('-').map((dart) => {
    if (dart === 'Bull') {
      return { type: 'DB', num: 25 };
    }

    return {
      type: dart[0],
      num: parseInt(dart.slice(1), 10),
    };
  });
}

function getModeForTarget(target) {
  if (target.type === 'D') {
    return 'checkout-double';
  }

  if (target.type === 'DB') {
    return 'checkout-bull';
  }

  if (target.type === 'S') {
    return 'checkout-single';
  }

  return 'checkout-triple';
}

function didHitTargetExactly(target, result) {
  if (target.type === 'T') {
    return result.score === target.num * 3 && !result.isDouble;
  }

  if (target.type === 'D') {
    return result.score === target.num * 2 && result.isDouble;
  }

  if (target.type === 'S') {
    return result.score === target.num && !result.isDouble;
  }

  return result.score === 50 && result.isDouble;
}

function selectSetupTarget(remaining, dartsLeft, profile) {
  if (remaining > 230) {
    return { type: 'T', num: 20 };
  }

  const skill = getSkillRatio(profile);
  const setupBias = profile.setupBias ?? 1;
  let bestTarget = SETUP_CANDIDATES[0];
  let bestRating = Number.NEGATIVE_INFINITY;

  for (const candidate of SETUP_CANDIDATES) {
    const intendedScore = getTargetScore(candidate);
    const leave = remaining - intendedScore;

    if (leave < 2 || leave === 1) {
      continue;
    }

    let rating = getLeaveQuality(leave);

    if (candidate.type === 'T') {
      rating += candidate.num === 20 ? 8 : candidate.num === 19 ? 6 : 4;
      if (remaining > 170) {
        rating += (2 + skill * 6) * setupBias;
      }
      if (dartsLeft === 1) {
        rating -= 10 - skill * 8;
        if (remaining <= 90) {
          rating -= 4 - skill * 3;
        }
      }
    }

    if (candidate.type === 'S') {
      rating += leave === 50 ? 10 : 2;
    }

    rating += intendedScore * setupBias * (remaining > 170
      ? (dartsLeft === 1 ? 0.14 + skill * 0.04 : 0.22 + skill * 0.06)
      : (dartsLeft === 1 ? 0.05 + skill * 0.03 : 0.10 + skill * 0.04));

    if (leave > 170 && dartsLeft === 1) {
      rating -= 18 - skill * 8;
    }

    if (rating > bestRating) {
      bestRating = rating;
      bestTarget = candidate;
    }
  }

  return bestTarget;
}

function selectTargetForDart(remaining, dartsLeft, profile) {
  if (remaining >= 2 && remaining <= 170) {
    const routes = getCheckoutOptions(remaining);
    if (routes.length > 0) {
      const parsedRoute = parseCheckoutRoute(routes[0]);
      if (parsedRoute.length <= dartsLeft) {
        const target = parsedRoute[0];
        return {
          target,
          mode: getModeForTarget(target),
          plannedRoute: parsedRoute,
        };
      }
    }
  }

  const target = selectSetupTarget(remaining, dartsLeft, profile);
  return {
    target,
    mode: target.type === 'T' ? 'scoring' : 'setup',
  };
}

function throwAtTarget(target, profile, turnForm, mode) {
  if (mode === 'checkout-double' && target.type === 'D') {
    return throwAtCheckoutDouble(target.num, profile, turnForm);
  }

  if (mode === 'checkout-bull') {
    return throwAtCheckoutBull(profile, turnForm);
  }

  if (target.type === 'S') {
    return throwAtSingleTarget(target.num, profile, turnForm, mode === 'checkout-single');
  }

  if (target.type === 'T') {
    return throwAtScoringTriple(target.num, profile, turnForm, mode === 'checkout-triple');
  }

  if (target.type === 'D') {
    return throwAtCheckoutDouble(target.num, profile, turnForm);
  }

  return throwAtCheckoutBull(profile, turnForm);
}

function simulateFullTurn(level, currentScore) {
  const profile = BOT_LEVELS[level];
  if (!profile) {
    return { score: 0, isCheckout: false };
  }

  let remaining = currentScore;
  let totalScored = 0;
  const turnForm = getTurnForm(profile);
  let activeRoute = null;

  for (let dart = 0; dart < 3; dart++) {
    const dartsLeft = 3 - dart;
    const selection = activeRoute && activeRoute.length <= dartsLeft
      ? {
          target: activeRoute[0],
          mode: getModeForTarget(activeRoute[0]),
          plannedRoute: activeRoute,
        }
      : selectTargetForDart(remaining, dartsLeft, profile);
    const { target, mode, plannedRoute } = selection;
    const result = throwAtTarget(target, profile, turnForm, mode);
    const newRemaining = remaining - result.score;

    if (newRemaining < 0 || newRemaining === 1) {
      return { score: 0, isCheckout: false };
    }

    if (newRemaining === 0 && result.isDouble) {
      return { score: totalScored + result.score, isCheckout: true, dartsUsed: dart + 1 };
    }

    if (newRemaining === 0 && !result.isDouble) {
      return { score: 0, isCheckout: false };
    }

    totalScored += result.score;
    remaining = newRemaining;
    activeRoute = plannedRoute && plannedRoute.length > 1 && didHitTargetExactly(target, result)
      ? plannedRoute.slice(1)
      : null;
  }

  return { score: totalScored, isCheckout: false };
}

export function generateBotScore(level, currentScore) {
  if (!BOT_LEVELS[level]) {
    return { score: 0, isCheckout: false };
  }

  return simulateFullTurn(level, currentScore);
}

export function generateClosingDarts(level, closingScore, dartsUsed) {
  if (dartsUsed && dartsUsed >= 1 && dartsUsed <= 3) {
    return dartsUsed;
  }

  const profile = BOT_LEVELS[level];
  if (!profile) {
    return 3;
  }

  const canOneDart = closingScore === 50 || (closingScore <= 40 && closingScore >= 2 && closingScore % 2 === 0);
  const canTwoDarts = closingScore <= 110 && closingScore >= 2;
  const oneDartRate = profile.accuracy * 0.55;
  const twoDartRate = profile.accuracy * 0.70;

  if (canOneDart && Math.random() < oneDartRate) return 1;
  if (canTwoDarts && Math.random() < twoDartRate) return 2;
  return 3;
}

export function getBotThinkingTime(level) {
  const base = 1500 + (8 - level) * 180;
  const variance = Math.random() * 600 - 300;
  return Math.max(800, Math.round(base + variance));
}

export function getBotName(level) {
  const names = {
    1: 'Bot Novato',
    2: 'Bot Amateur',
    3: 'Bot Liga',
    4: 'Bot Avanzado',
    5: 'Bot Experto',
    6: 'Bot Pro',
    7: 'Bot Leyenda',
    8: '???',
  };

  return names[level] || 'Bot';
}